import {Component, ElementRef, forwardRef, Host, Injector, Input, OnInit, Optional, SkipSelf, ViewChild} from '@angular/core';
import {ControlContainer, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ValueAccessorAbstract} from '@wba-ui-module/components/formfields/commons/value-accessor.abstract';
import {EventUtils} from '@wba/utils/event.utils';
import {FormUtils} from '@wba/utils/form.utils';
import {MaskedNumberOptions} from 'imask/masked/number';
import {defaultMaskedNumberParams, getMaskedNumberOptions} from '@wba-ui-module/components/formfields/getMaskedNumberOptions';
import {NUMBER_TYPE} from '@wba/models/enums/zahlentyp';
import DOMPurify from 'dompurify';

@Component({
    selector: 'wba-input-text',
    templateUrl: './input.component.html',
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }],
    standalone: false
})
export class InputComponent extends ValueAccessorAbstract implements OnInit {

    @Input() id: string;
    /** Ist immer, wenn im Eingabefeld eine Zahl erwartet wird, zu setzen */
    @Input() typ: NUMBER_TYPE;
    @Input() name: string;
    @Input() label: string;
    @Input() placeholder = '';
    @Input() ariaLabel = '';
    @Input() title = '';
    @Input() inputType = 'text';  // wird niemals in den Components überladen
    /** die maxLength gilt nur bei Eingabetyp "text" */
    @Input() maxLength = 100;
    @Input() required: boolean;
    /** die digitsBeforeDecimal gelten nur bei Eingabetyp "number" */
    @Input() digitsBeforeDecimal = 5;
    @Input() hinweis: { target: string, modal: string };
    @Input() unitLabel = '';
    @Input() customMaskOptions: Partial<MaskedNumberOptions> = defaultMaskedNumberParams.customMaskOptions;
    @Input() allowDecimal: boolean = defaultMaskedNumberParams.allowDecimal;              // default: true
    @Input() allowNegatives: boolean = defaultMaskedNumberParams.allowNegatives;          // default: false
    @Input() isNegativeRequired: boolean = defaultMaskedNumberParams.isNegativeRequired;  // default: false
    @Input() thousandsSeparatorSymbol: string = defaultMaskedNumberParams.thousandsSeparatorSymbol;   // default: '.'
    @ViewChild('input') el: ElementRef;

    private readonly gleitkommaZahlenZweistellig: Set<NUMBER_TYPE> = new Set([NUMBER_TYPE.WAEHRUNG, NUMBER_TYPE.QUADRATMETER, NUMBER_TYPE.ANZ_RAEUME]);

    constructor(@Optional() @Host() @SkipSelf() private readonly controlContainer: ControlContainer,
                private readonly eventUtils: EventUtils,
                private readonly elementRef: ElementRef,
                protected injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.controlContainer) {
            if (this.formControlName) {
                this.control = this.controlContainer.control.get(this.formControlName) as FormControl;
            }
        }

        if (this.typ == undefined) {
            return;
        }

        if (NUMBER_TYPE.JAHR == this.typ || NUMBER_TYPE.ANZAHL == this.typ) {
            this.allowDecimal = false;
            this.allowNegatives = false;
            this.thousandsSeparatorSymbol = '';
        }

        if (this.gleitkommaZahlenZweistellig.has(this.typ)) {
            // Währung + Quadratmeter + Räume können eine beliebige 2-stellige Kommazahl haben
            this.allowDecimal = true;
            this.allowNegatives = false;
            this.customMaskOptions = {scale: 2};
        }
    }

    // Set touched on blur
    onBlur() {
        this.onTouchedCallback();
        if (this.value?.length) {
            const sanitized = DOMPurify.sanitize(this.value, {
                ALLOWED_TAGS: [],
                ALLOWED_ATTR: []
            });
            this.value = FormUtils.formValueSingleWhitespaces(sanitized);
        }
        this.eventUtils.focusOut$.next(this.elementRef);
    }
    get maskOptions(): MaskedNumberOptions {
        if (this.typ == undefined) {
            return null;
        }
        return getMaskedNumberOptions({
            isNegativeRequired: this.isNegativeRequired,
            thousandsSeparatorSymbol: this.thousandsSeparatorSymbol,
            allowDecimal: this.allowDecimal,
            allowNegatives: this.allowNegatives,
            customMaskOptions: this.customMaskOptions,

        });
    }
}
